function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useState } from 'react';
import { DataGrid } from 'react-data-grid';
import { DataGridHeader } from './datagrid/DataGridHeader';
import { useDeleteColumn } from './datagrid/useDeleteColumn';
import { useDataGridFiltering } from './datagrid/useDataGridFiltering';
import { useDataGridSorting } from './datagrid/useDataGridSorting';
import { useDataGridRowExpansion } from './datagrid/useDataGridRowExpansion';
import { useDataGridColumns } from './datagrid/useDataGridColumns';
import { useTransformHeadersToColumns } from './datagrid/useTransformHeadersToColumns';
import { useProcessedRows } from './datagrid/useProcessedRows';
import { useTranslation } from 'react-i18next';
import MultiColumnSort from './MultiColumnSort';
import TableColumnFilter, { hasFilterableHeaders } from './TableColumnFilter';
import TableColumnSelector from './TableColumnSelector';
import { ToggleExpandCollapseButton } from '../buttons/ToggleExpandCollapseButton';
import './DataGridTable.css';

/**
 * Generic DataGrid wrapper component that provides a reusable abstraction
 * over react-data-grid library.
 * 
 * This component handles:
 * - Column rendering (headers are transformed to columns internally)
 * - Row rendering  
 * - Sorting (built-in support using column.valueFnForSortAndFilter)
 * - Filtering (built-in support using column.valueFnForSortAndFilter)
 * - Row expansion with lazy-loaded child rows
 * - Custom cell and header renderers
 * - Styling
 * - Delete column
 * 
 * @param {Array} headers - Header definitions. Each header can include any react-data-grid column properties:
 *                          - text: Column header text (becomes 'name')
 *                          - width: Column width
 *                          - sortable: Enable sorting for this column
 *                          - valueFnForSortAndFilter: Function to extract value for sorting/filtering
 *                          - filterableByText: Whether column is filterable
 *                          - renderCell: Custom cell renderer
 *                          - renderEditCell: Custom edit cell renderer
 *                          - editable: Whether cells are editable
 *                          - ...any other column properties
 * @param {Array} rows - Row data
 * @param {string} title - Optional title rendered in the table controls line
 * @param {Object} totalsRow - Optional hash mapping header text to totals value for rendering totals row
 * @param {Function} rowKeyGetter - Function to get unique key for each row
 * @param {Function} onRowsChange - Callback when rows are modified
 * @param {boolean} enableSorting - Enable built-in sorting (default: false)
 * @param {boolean} useMultiColumnSorting - Show the multi-column sorting controls and disable header sorting
 * @param {Function} onSort - Optional backend sorting callback receiving [{ headerIndex, direction }]
 * @param {boolean} enableFiltering - Enable built-in filtering (default: false)
 * @param {boolean} useFilterSetup - Show the filter setup above the grid instead of header filters
 * @param {boolean} enableRowExpansion - Enable row expansion (default: false)
 * @param {Function} onRowExpand - Async callback(row) => childRows[] when row is expanded
 * @param {Function} getChildRows - Optional function(row) => childRows[] to get fresh child rows (for reactive updates)
 * @param {Function} isRowExpandable - Function(row) => boolean to determine if row can be expanded
 * @param {string} expandedRowProp - Property name to track expansion state on row (default: 'row_expanded')
 * @param {string} childRowProp - Property name to mark child rows (default: 'is_child_row')
 * @param {number} headerRowHeight - Height of header row in pixels
 * @param {string} className - CSS class name for the grid
 * @param {Object} style - Inline styles for the grid
 * @param {ReactNode} headerContent - Optional custom content to render above the grid (overrides built-in header)
 * @param {boolean} showAddRowButton - Whether to show add row button in built-in header
 * @param {Function} onAddRow - Callback when add row button is clicked
 * @param {string} addRowButtonText - Text for add row button
 * @param {boolean} addRowButtonDisabled - Whether add row button is disabled
 * @param {boolean} showLoadingAlert - Whether to show loading alert in built-in header
 * @param {string} loadingAlertMessage - Message for loading alert
 * @param {boolean} showDeleteColumn - Whether to show delete column
 * @param {Function} onDeleteRow - Callback when delete is confirmed, receives the row
 * @param {boolean|Function} deleteRowDisabled - Whether delete button should be disabled, or a function receiving the row
 * @param {Function} shouldHideDeleteForRow - Function that receives row and returns true if delete should be hidden
 * @param {string|Function} deleteRowTooltipText - Delete button tooltip text, or a function receiving the row
 * @param {string} deleteConfirmMessage - Confirmation message for delete
 * @param {number|Function} rowHeight - Height for data rows. Can be a number or function(row) => number
 */
export default function DataGridTable(_ref) {
  let {
    headers,
    rows,
    title = '',
    totalsRow,
    rowKeyGetter = row => row.id,
    onRowsChange,
    enableSorting = true,
    useMultiColumnSorting = false,
    onSort,
    enableFiltering = true,
    useFilterSetup = false,
    tableColumnSelectorProps = null,
    enableRowExpansion = true,
    onRowExpand,
    getChildRows,
    isRowExpandable = () => false,
    expandedRowProp = 'row_expanded',
    childRowProp = 'is_expanded_row_child_row',
    headerRowHeight = 80,
    rowHeight,
    className = 'rdg-light',
    style = {
      height: '800px'
    },
    headerContent = null,
    showAddRowButton = false,
    // show an Add Row button above the table
    onAddRow,
    addRowButtonText = null,
    // set with translation
    addRowButtonDisabled = false,
    showLoadingAlert = false,
    loadingAlertMessage = null,
    // set with translation
    showDeleteColumn = false,
    // show a delete button at the end of each row
    onDeleteRow,
    deleteRowDisabled = false,
    // if you want to hide the delete button for specific rows. By default - hides it for the totals row if such exists
    shouldHideDeleteForRow = row => row.isTotalsRow,
    deleteRowTooltipText = null,
    deleteConfirmMessage = 'Are you sure you want to delete this row?'
  } = _ref;
  console.log('Data Grid Table Rerender');
  const {
    t
  } = useTranslation();
  const [isTableColumnSelectorExpanded, setIsTableColumnSelectorExpanded] = useState(false);
  const [isMultiColumnSortExpanded, setIsMultiColumnSortExpanded] = useState(false);
  const [isTableColumnFilterExpanded, setIsTableColumnFilterExpanded] = useState(false);
  addRowButtonText ||= t('table_add_row');
  loadingAlertMessage ||= t('table_loading_message');
  const excludeTotalsRowAndUpdateRows = useCallback(updatedRows => {
    const updatesMap = new Map();
    updatedRows.forEach(row => {
      if (!row.isTotalsRow) {
        updatesMap.set(row.id, row);
      }
    });
    onRowsChange(updatesMap);
  }, [onRowsChange]);

  // Transform headers into column definitions
  const columns = useTransformHeadersToColumns(headers, totalsRow);

  // Use custom hooks for filtering, sorting, and row expansion
  const {
    filters,
    updateFilter,
    applyFilterValues,
    applyFilters
  } = useDataGridFiltering(enableFiltering, columns);
  const shouldSortLocally = enableSorting && !onSort;
  const {
    sortColumns,
    setSortColumns,
    applySorting,
    sortRowsSubset
  } = useDataGridSorting(shouldSortLocally, columns);
  const {
    toggleRowExpansion,
    insertChildRows
  } = useDataGridRowExpansion(enableRowExpansion, onRowExpand, rowKeyGetter, expandedRowProp, childRowProp, getChildRows, sortRowsSubset, applyFilters);

  // Enhance columns with filtering headers and row expansion support
  const enhancedColumns = useDataGridColumns(columns, enableFiltering, filters, updateFilter, enableRowExpansion, toggleRowExpansion, isRowExpandable, expandedRowProp, !useFilterSetup);

  // Conditionally add delete column
  const finalColumns = useDeleteColumn(enhancedColumns, {
    showDeleteColumn,
    onDelete: onDeleteRow,
    disabled: deleteRowDisabled,
    shouldHideForRow: shouldHideDeleteForRow,
    tooltipText: deleteRowTooltipText,
    confirmMessage: deleteConfirmMessage
  });
  const displayedColumns = useMultiColumnSorting ? finalColumns.map(column => ({
    ...column,
    sortable: false
  })) : finalColumns;
  const updateSortColumns = useCallback(updatedSortColumns => {
    setSortColumns(updatedSortColumns);
    if (onSort) {
      onSort(updatedSortColumns.map(_ref2 => {
        let {
          columnKey,
          direction
        } = _ref2;
        return {
          headerIndex: parseInt(columnKey.replace('col_', '')),
          direction: direction === 'ASC' ? 'asc' : 'desc'
        };
      }));
    }
  }, [onSort, setSortColumns]);
  const applyMultiColumnSort = useCallback(sorts => {
    const updatedSortColumns = sorts.map(_ref3 => {
      let {
        headerIndex,
        direction
      } = _ref3;
      return {
        columnKey: `col_${headerIndex}`,
        direction: direction === 'asc' ? 'ASC' : 'DESC'
      };
    });
    updateSortColumns(updatedSortColumns);
  }, [updateSortColumns]);

  // Apply filtering, sorting, and row expansion to rows
  const processedRows = useProcessedRows(rows, totalsRow, applyFilters, applySorting, insertChildRows);
  const shouldRenderMultiColumnSort = useMultiColumnSorting;
  const shouldRenderTableColumnFilter = enableFiltering && useFilterSetup && hasFilterableHeaders(columns);
  const shouldRenderTableColumnSelector = !!tableColumnSelectorProps;
  const renderTableControls = () => {
    const controls = [];
    if (shouldRenderTableColumnSelector) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        "data-testid": "datagrid-table-columns-toggle",
        expanded: isTableColumnSelectorExpanded,
        key: "columns",
        onClick: () => setIsTableColumnSelectorExpanded(!isTableColumnSelectorExpanded),
        text: "Columns",
        type: "button"
      }));
    }
    if (shouldRenderMultiColumnSort) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        "data-testid": "datagrid-table-sort-toggle",
        expanded: isMultiColumnSortExpanded,
        key: "sort",
        onClick: () => setIsMultiColumnSortExpanded(!isMultiColumnSortExpanded),
        text: "Sort",
        type: "button"
      }));
    }
    if (shouldRenderTableColumnFilter) {
      controls.push(/*#__PURE__*/React.createElement(ToggleExpandCollapseButton, {
        "data-testid": "datagrid-table-filter-toggle",
        expanded: isTableColumnFilterExpanded,
        key: "filter",
        onClick: () => setIsTableColumnFilterExpanded(!isTableColumnFilterExpanded),
        text: "Filter",
        type: "button"
      }));
    }
    if (controls.length === 0) {
      return null;
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "TableControlsLegend mb-3"
    }, /*#__PURE__*/React.createElement("div", {
      className: "d-flex align-items-center flex-wrap"
    }, /*#__PURE__*/React.createElement("div", {
      className: "border-top",
      style: {
        width: '12px'
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "d-flex align-items-center flex-wrap gap-2 px-2",
      style: {
        minWidth: 0
      }
    }, !!title && /*#__PURE__*/React.createElement("span", {
      className: "font-weight-bold",
      style: {
        overflowWrap: 'anywhere'
      }
    }, title), controls), /*#__PURE__*/React.createElement("div", {
      className: "border-top flex-grow-1"
    })), shouldRenderTableColumnSelector && /*#__PURE__*/React.createElement(TableColumnSelector, _extends({}, tableColumnSelectorProps, {
      isExpanded: isTableColumnSelectorExpanded
    })), shouldRenderMultiColumnSort && /*#__PURE__*/React.createElement(MultiColumnSort, {
      headers: headers,
      isExpanded: isMultiColumnSortExpanded,
      onSort: applyMultiColumnSort
    }), shouldRenderTableColumnFilter && /*#__PURE__*/React.createElement(TableColumnFilter, {
      headers: columns,
      filters: filters,
      getFilterKey: column => column.key,
      isExpanded: isTableColumnFilterExpanded,
      onApplyFilters: applyFilterValues
    }));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderTableControls(), /*#__PURE__*/React.createElement(DataGridHeader, {
    headerContent: headerContent,
    showLoadingAlert: showLoadingAlert,
    loadingMessage: loadingAlertMessage,
    showAddButton: showAddRowButton,
    addButtonText: addRowButtonText,
    onAddRow: onAddRow,
    addButtonDisabled: addRowButtonDisabled
  }), /*#__PURE__*/React.createElement(DataGrid, {
    columns: displayedColumns,
    rows: processedRows,
    rowKeyGetter: rowKeyGetter,
    onRowsChange: excludeTotalsRowAndUpdateRows,
    sortColumns: enableSorting ? sortColumns : undefined,
    onSortColumnsChange: enableSorting && !useMultiColumnSorting ? updateSortColumns : undefined,
    headerRowHeight: headerRowHeight,
    rowHeight: rowHeight,
    className: className,
    style: style
  }));
}